import React, { useState, useEffect } from 'react';
import {
  Users, Calendar, CreditCard, TrendingUp, TrendingDown, Activity,
  Clock, CheckCircle, AlertTriangle, Percent,
  ChevronLeft, ChevronRight, Download, Building, X, Coins, FileText, HelpCircle
} from 'lucide-react';
import api from '../services/api';
import { Toast } from '../components/ui/Toast';
import { useLoader } from '../contexts/LoaderContext';

import { useFeatureFlag } from '../hooks/useFeatureFlag';
import GuideRapportsStatistiques from './GuideRapportsStatistiques';
import Pagination from '../components/ui/Pagination';

const RapportsRH = () => {
  // États
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('jour');
  const [employesFantomes, setEmployesFantomes] = useState(null);
  const [pageFantomesJamais, setPageFantomesJamais] = useState(1);
  const [pageFantomesInactifs, setPageFantomesInactifs] = useState(1);
  const LIMIT_FANTOMES = 10;
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [showGuide, setShowGuide] = useState(false);

  const { showLoader, hideLoader } = useLoader();

  // 🔥 Feature Flag Compta
  const { isEnabled: comptaEnabled } = useFeatureFlag('compta_module');
  const { isEnabled: fantomesEnabled } = useFeatureFlag('employes_fantomes');

  // Données
  const [rapportJour, setRapportJour] = useState(null);
  const [rapportMois, setRapportMois] = useState(null);
  const [statistiquesGlobales, setStatistiquesGlobales] = useState(null);
  const [statistiquesPaiements, setStatistiquesPaiements] = useState(null);
  const [statistiquesAvances, setStatistiquesAvances] = useState(null);


  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // Formatage dates
  const formatDateForDisplay = (date) => {
    if (viewMode === 'jour') {
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
      });
    } else {
      return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    }
  };

  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const goToPreviousMonth = () => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setSelectedDate(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Charger les données
  useEffect(() => {
    if (viewMode === 'jour') {
      loadRapportJournalier();
    } else if (viewMode === 'mois') {
      loadRapportMensuel();
      loadStatistiquesMensuelles();
    } else if (viewMode === 'fantomes') {
      loadEmployesFantomes();
    }
  }, [selectedDate, viewMode]);

  const loadRapportJournalier = async () => {
    showLoader();
    setLoading(true);
    try {
      const dateStr = formatDateForAPI(selectedDate);
      const response = await api.rapports.getRapportJournalier(dateStr);
      if (response.success) {
        setRapportJour(response.data);
      }
    } catch (error) {
      console.error('❌ Erreur chargement rapport journalier:', error);
      showToast('Erreur lors du chargement du rapport', 'error');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const loadRapportMensuel = async () => {
    showLoader();
    setLoading(true);
    try {
      const mois = selectedDate.getMonth() + 1;
      const annee = selectedDate.getFullYear();
      const response = await api.rapports.getRapportMensuel(mois, annee);
      if (response.success) {
        setRapportMois(response.data);
      }
    } catch (error) {
      console.error('❌ Erreur chargement rapport mensuel:', error);
      showToast('Erreur lors du chargement du rapport', 'error');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const loadStatistiquesMensuelles = async () => {
    try {
      const mois = selectedDate.getMonth() + 1;
      const annee = selectedDate.getFullYear();

      const globalesResponse = await api.rapports.getStatistiquesGlobales(mois, annee);
      if (globalesResponse.success) setStatistiquesGlobales(globalesResponse.data);

      const paiementsResponse = await api.rapports.getStatistiquesPaiements(mois, annee);
      if (paiementsResponse.success) setStatistiquesPaiements(paiementsResponse.data);

      const avancesResponse = await api.rapports.getStatistiquesAvances(mois, annee);
      if (avancesResponse.success) setStatistiquesAvances(avancesResponse.data);
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
    }
  };


  const loadEmployesFantomes = async () => {
    showLoader();
    setLoading(true);
    try {
      const response = await api.rapports.getEmployesFantomes();
      if (response.success) setEmployesFantomes(response.data);
    } catch (error) {
      console.error('❌ Erreur chargement employés fantômes:', error);
      showToast('Erreur lors du chargement', 'error');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };


  // ============================================================
  // EXPORT PDF
  // ============================================================

  const handleExportPDF = async () => {
    if (exportLoading) return;
    setExportLoading(true);

    try {
      showToast('Génération du rapport PDF en cours...', 'loading');

      if (viewMode === 'jour') {
        const dateStr = formatDateForAPI(selectedDate);
        await api.rapports.exportPdfJournalier(dateStr);
      } else {
        const mois = selectedDate.getMonth() + 1;
        const annee = selectedDate.getFullYear();
        await api.rapports.exportPdfMensuel(mois, annee);
      }

      showToast('Rapport PDF téléchargé avec succès !', 'success');
    } catch (error) {
      console.error('❌ Erreur export PDF:', error);
      showToast('Erreur lors de la génération du rapport', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  // Composant Carte Indicateur
  const CardIndicateur = ({ icon: Icon, label, value, subValue, color, trend }) => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 transition-all duration-200 hover:shadow-lg hover:-translate-y-1">
      <div className="flex items-start justify-between mb-4">
        <div className="p-3 rounded-xl shadow-sm" style={{ backgroundColor: color }}>
          <Icon className="text-white" size={24} />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center text-xs font-semibold ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-600'
            }`}>
            {trend > 0 && <TrendingUp className="w-4 h-4 mr-1" />}
            {trend < 0 && <TrendingDown className="w-4 h-4 mr-1" />}
            {trend !== 0 ? `${Math.abs(trend)}%` : 'Stable'}
          </div>
        )}
      </div>
      <div className="text-xs font-semibold text-gray-600 mb-2">{label}</div>
      <div className="text-3xl font-bold text-gray-900 mb-1">{value}</div>
      {subValue && <div className="text-xs font-medium text-gray-600">{subValue}</div>}
    </div>
  );

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>

      {/* En-tête */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2" style={{ color: "#448696" }}>
          Rapports & Statistiques
        </h1>
        <p className="text-gray-600 text-base font-medium">
          {viewMode === 'jour' ? 'Suivi journalier des indicateurs' : 'Analyse des performances et suivi des indicateurs'}
        </p>
      </div>

      {/* Navigation */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">

          {/* Sélecteur de mode */}
          <div className="inline-flex gap-2 bg-gray-100 rounded-xl p-1.5">
            <button
              onClick={() => setViewMode('jour')}
              className={`flex items-center gap-2 px-4 py-2 font-semibold text-sm transition-all duration-200 rounded-lg ${viewMode === 'jour' ? 'text-white shadow-md' : 'text-gray-600'
                }`}
              style={{ backgroundColor: viewMode === 'jour' ? '#448696' : 'transparent' }}
            >
              <Calendar size={18} />
              Vue Journalière
            </button>

            <button
              onClick={() => setViewMode('mois')}
              className={`flex items-center gap-2 px-4 py-2 font-semibold text-sm transition-all duration-200 rounded-lg ${viewMode === 'mois' ? 'text-gray-900 shadow-md' : 'text-gray-600'
                }`}
              style={{ backgroundColor: viewMode === 'mois' ? '#fed774' : 'transparent' }}
            >
              <Activity size={18} />
              Vue Mensuelle
            </button>

            {fantomesEnabled && (
              <button
                onClick={() => setViewMode('fantomes')}
                className={`flex items-center gap-2 px-4 py-2 font-semibold text-sm transition-all duration-200 rounded-lg ${viewMode === 'fantomes' ? 'text-white shadow-md' : 'text-gray-600'
                  }`}
                style={{ backgroundColor: viewMode === 'fantomes' ? '#8b5158' : 'transparent' }}
              >
                <Users size={18} />
                Employés fantômes
              </button>
            )}

          </div>


          {/* Navigation date + bouton PDF */}
          {/* Navigation date + bouton PDF */}
          <div className="flex items-center gap-3">

            {/* Sélecteur de date — masqué sur vue fantômes */}
            {viewMode !== 'fantomes' && (
              <>
                <button
                  onClick={viewMode === 'jour' ? goToPreviousDay : goToPreviousMonth}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="px-4 py-2 bg-gray-50 rounded-lg min-w-[250px] text-center">
                  <span className="text-sm font-bold text-gray-900 capitalize">
                    {formatDateForDisplay(selectedDate)}
                  </span>
                </div>

                <button
                  onClick={viewMode === 'jour' ? goToNextDay : goToNextMonth}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                <button
                  onClick={goToToday}
                  className="px-3 py-2 text-xs text-white rounded-lg font-semibold shadow-sm hover:shadow transition-all"
                  style={{ backgroundColor: '#448696' }}
                >
                  Aujourd'hui
                </button>

                {/* Bouton Export PDF */}
                <button
                  onClick={handleExportPDF}
                  disabled={exportLoading || loading}
                  className="flex items-center gap-2 px-3 py-2 text-xs rounded-lg font-semibold shadow-sm hover:shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#fed774', color: '#1f2937' }}
                >
                  <FileText size={14} />
                  Export PDF
                </button>
              </>
            )}

            <button
              onClick={() => setShowGuide(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:text-[#448696] hover:bg-[#448696]/10 transition-all border border-gray-200 hover:border-[#448696]/30"
            >
              <HelpCircle size={14} />
              Guide de lecture
            </button>

          </div>


        </div>
      </div>

      {loading ? null : (
        <>
          {/* VUE JOURNALIÈRE */}
          {viewMode === 'jour' && rapportJour && (
            <div className="space-y-8">

              {/* Indicateurs du jour */}
              <div>
                <div className="px-6 py-4 mb-4 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
                  <Activity size={24} className="text-white" />
                  <h2 className="font-bold text-white text-xl">Vue d'ensemble du jour</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <CardIndicateur
                    icon={Users}
                    label="Employés actifs"
                    value={rapportJour.employes_actifs}
                    subValue="Total dans l'entreprise"
                    color="#448696"
                  />
                  <CardIndicateur
                    icon={CheckCircle}
                    label="Présents"
                    value={rapportJour.presents}
                    subValue={`${rapportJour.taux_presence}% de présence`}
                    color="#22c55e"
                  />
                  <CardIndicateur
                    icon={X}
                    label="Absents"
                    value={rapportJour.absents || rapportJour.total_absents || 0}
                    subValue={`${100 - rapportJour.taux_presence}% d'absence`}
                    color="#ef4444"
                  />
                  <CardIndicateur
                    icon={Clock}
                    label="Retards"
                    value={rapportJour.retards || 0}
                    subValue="Pointages en retard"
                    color="#f59e0b"
                  />
                </div>
              </div>

              {/* Détails du jour */}
              <div>
                <div className="px-6 py-4 mb-4 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#fed774' }}>
                  <Clock size={24} className="text-gray-900" />
                  <h2 className="font-bold text-gray-900 text-xl">Détails de la journée</h2>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-4 rounded-xl border border-gray-100">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-gray-600">Taux de présence</span>
                        <span className="text-2xl font-bold text-green-600">{rapportJour.taux_presence}%</span>
                      </div>
                      <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${rapportJour.taux_presence}%`, backgroundColor: '#22c55e' }}></div>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        {rapportJour.presents} employés présents sur {rapportJour.employes_actifs}
                      </p>
                    </div>

                    <div className="p-4 rounded-xl border border-gray-100">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-gray-600">Taux d'absence</span>
                        <span className="text-2xl font-bold text-red-600">{100 - rapportJour.taux_presence}%</span>
                      </div>
                      <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${100 - rapportJour.taux_presence}%`, backgroundColor: '#ef4444' }}></div>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        {rapportJour.absents || rapportJour.total_absents || 0} employés absents
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* VUE MENSUELLE */}
          {viewMode === 'mois' && (
            <div className="space-y-8">

              {/* Section 1: Indicateurs Globaux */}
              {statistiquesGlobales && (
                <div>
                  <div className="px-6 py-4 mb-4 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
                    <Activity size={24} className="text-white" />
                    <h2 className="font-bold text-white text-xl">Vue d'ensemble</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <CardIndicateur icon={Users} label="Employés actifs" value={statistiquesGlobales.nombre_employes} subValue={`${statistiquesGlobales.nombre_departements} départements`} color="#448696" />
                    <CardIndicateur icon={Coins} label="Masse salariale" value={`${statistiquesGlobales.masse_salariale.toLocaleString()} FCFA`} subValue="Total mensuel" color="#fed774" />
                    <CardIndicateur icon={CheckCircle} label="Taux de présence" value={`${statistiquesGlobales.taux_presence}%`} subValue="Moyenne du mois" color="#22c55e" />
                    <CardIndicateur icon={Activity} label="Jours analysés" value={rapportMois?.jours?.length || 0} subValue="Jours du mois" color="#448696" />
                  </div>
                </div>
              )}

              {/* Section 2: Module Paie - Conditionnel (Compta Module) */}
              {comptaEnabled && statistiquesPaiements && (
                <div>
                  <div className="px-6 py-4 mb-4 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
                    <CreditCard size={24} className="text-white" />
                    <h2 className="font-bold text-white text-xl">Gestion de la paie</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <CardIndicateur icon={CreditCard} label="Paiements effectués" value={statistiquesPaiements.nombre_paiements} subValue={`Sur ${statistiquesPaiements.nombre_employes} employés`} color="#448696" />
                    <CardIndicateur icon={Coins} label="Montant total payé" value={`${statistiquesPaiements.montant_total.toLocaleString()} FCFA`} subValue="Salaires nets" color="#22c55e" />
                    <CardIndicateur icon={AlertTriangle} label="En attente" value={statistiquesPaiements.paiements_en_attente} subValue="À valider" color="#f59e0b" />
                    <CardIndicateur icon={Percent} label="Taux de couverture" value={`${statistiquesPaiements.taux_couverture}%`} subValue="Employés payés" color="#448696" />
                  </div>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                      <h3 className="font-bold text-gray-900 text-sm mb-4">Composition de la paie</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Salaires bruts</span>
                          <span className="text-sm font-bold text-gray-900">{statistiquesPaiements.montant_brut.toLocaleString()} FCFA</span>
                        </div>
                        <div className="flex items-center justify-between text-red-600">
                          <span className="text-sm font-medium">Retenues</span>
                          <span className="text-sm font-bold">- {statistiquesPaiements.montant_retenues.toLocaleString()} FCFA</span>
                        </div>
                        <div className="flex items-center justify-between text-orange-600">
                          <span className="text-sm font-medium">Avances</span>
                          <span className="text-sm font-bold">- {statistiquesPaiements.montant_avances.toLocaleString()} FCFA</span>
                        </div>
                        <div className="pt-3 border-t-2 flex items-center justify-between" style={{ borderColor: '#448696' }}>
                          <span className="text-sm font-bold text-gray-900">Salaires nets</span>
                          <span className="text-xl font-bold" style={{ color: '#448696' }}>{statistiquesPaiements.montant_total.toLocaleString()} FCFA</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                      <h3 className="font-bold text-gray-900 text-sm mb-4">Statut des paiements</h3>
                      <div className="space-y-4">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Clôturés</span>
                            <span className="text-sm font-bold text-green-600">{statistiquesPaiements.paiements_clotures}</span>
                          </div>
                          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${(statistiquesPaiements.paiements_clotures / statistiquesPaiements.nombre_employes) * 100}%`, backgroundColor: '#22c55e' }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">En attente</span>
                            <span className="text-sm font-bold text-orange-600">{statistiquesPaiements.paiements_en_attente}</span>
                          </div>
                          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${(statistiquesPaiements.paiements_en_attente / statistiquesPaiements.nombre_employes) * 100}%`, backgroundColor: '#f59e0b' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}


              {/* Section 3: Module Avances - Conditionnel (Compta Module) */}
              {comptaEnabled && statistiquesAvances && (
                <div>
                  <div className="px-6 py-4 mb-4 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#fed774' }}>
                    <Coins size={24} className="text-gray-900" />
                    <h2 className="font-bold text-gray-900 text-xl">Avances sur salaire</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <CardIndicateur icon={Coins} label="Avances accordées" value={statistiquesAvances.nombre_avances} subValue={`${statistiquesAvances.nombre_employes} employés`} color="#fed774" />
                    <CardIndicateur icon={Coins} label="Montant total" value={`${statistiquesAvances.montant_total.toLocaleString()} FCFA`} subValue="Avances du mois" color="#448696" />
                    <CardIndicateur icon={Percent} label="Taux moyen" value={`${statistiquesAvances.taux_moyen}%`} subValue="Du salaire de base" color="#f59e0b" />
                  </div>
                </div>
              )}

              {/* Section 4: Calendrier du mois */}
              {rapportMois && (
                <div>
                  <div className="px-6 py-4 mb-4 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
                    <Calendar size={24} className="text-white" />
                    <h2 className="font-bold text-white text-xl">Calendrier de présence du mois</h2>
                  </div>

                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">

                    {/* Légende */}
                    <div className="mb-6 flex items-center gap-6 p-4 bg-gray-50 rounded-xl">
                      <div className="text-sm font-semibold text-gray-700">Légende :</div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-green-500"></div>
                        <span className="text-xs font-medium text-gray-600">≥ 90% : Excellent</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-yellow-500"></div>
                        <span className="text-xs font-medium text-gray-600">70-89% : Acceptable</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-red-500"></div>
                        <span className="text-xs font-medium text-gray-600">&lt; 70% : Critique</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-gray-300"></div>
                        <span className="text-xs font-medium text-gray-600">Week-end</span>
                      </div>
                    </div>

                    {/* Tableau */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b-2" style={{ borderColor: '#448696' }}>
                            <th className="text-left py-3 px-4 text-sm font-bold text-gray-700">Date</th>
                            <th className="text-center py-3 px-4 text-sm font-bold text-gray-700">
                              <div className="flex items-center justify-center gap-1">
                                <Users size={16} style={{ color: '#448696' }} /> Actifs
                              </div>
                            </th>
                            <th className="text-center py-3 px-4 text-sm font-bold text-green-700">
                              <div className="flex items-center justify-center gap-1">
                                <CheckCircle size={16} /> Présents
                              </div>
                            </th>
                            <th className="text-center py-3 px-4 text-sm font-bold text-red-700">
                              <div className="flex items-center justify-center gap-1">
                                <X size={16} /> Absents
                              </div>
                            </th>
                            <th className="text-center py-3 px-4 text-sm font-bold text-orange-700">
                              <div className="flex items-center justify-center gap-1">
                                <Clock size={16} /> Retards
                              </div>
                            </th>
                            <th className="text-center py-3 px-4 text-sm font-bold text-gray-700">Taux présence</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rapportMois.jours.map((jour, index) => {
                            const jourDate = new Date(jour.date);
                            const isToday = formatDateForAPI(jourDate) === formatDateForAPI(new Date());
                            const isWeekend = jourDate.getDay() === 0 || jourDate.getDay() === 6;
                            const absents = jour.total_absents || (jour.employes_actifs - jour.presents);
                            const isFuture = jour.is_future || false;

                            return (
                              <tr
                                key={index}
                                className={`border-b border-gray-100 transition-colors ${isToday ? 'bg-blue-50' : ''
                                  } ${isWeekend ? 'bg-gray-50 opacity-60' : isFuture ? 'bg-gray-50 opacity-40' : 'hover:bg-gray-50'}`}
                              >
                                <td className="py-4 px-4">
                                  <div className="flex items-center gap-3">
                                    {isToday && <div className="w-2 h-2 rounded-full bg-blue-500"></div>}
                                    <div>
                                      <div className="text-sm font-bold text-gray-900">
                                        {jourDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                      </div>
                                      <div className={`text-xs font-medium capitalize ${isWeekend ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {jour.jour_semaine}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="text-center py-4 px-4">
                                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gray-100 text-gray-900 font-bold text-lg">{jour.employes_actifs}</span>
                                </td>
                                <td className="text-center py-4 px-4">
                                  {isFuture ? <span className="text-xs text-gray-400 font-medium">-</span> : (
                                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-green-100 text-green-700 font-bold text-lg">{jour.presents}</span>
                                  )}
                                </td>
                                <td className="text-center py-4 px-4">
                                  {isFuture ? <span className="text-xs text-gray-400 font-medium">-</span> : (
                                    <div className="relative group flex flex-col items-center gap-1">
                                      <span className={`inline-flex items-center justify-center w-12 h-12 rounded-xl font-bold text-lg ${(jour.total_absents || absents) === 0 ? 'bg-gray-50 text-gray-300' : 'bg-red-100 text-red-700'
                                        }`}>{jour.total_absents || absents}</span>
                                      {(jour.absents_pointes > 0 || jour.aucun_pointage > 0) && (
                                        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-3 hidden group-hover:block z-50 border border-red-200">
                                          <div className="flex items-start gap-2">
                                            <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                            <div>
                                              <p className="font-semibold text-red-700 mb-1">Détail des absences</p>
                                              <div className="text-gray-600 space-y-1">
                                                <p><span className="font-semibold text-red-600">{jour.absents_pointes || 0}</span> employé{(jour.absents_pointes || 0) > 1 ? 's' : ''} pointé{(jour.absents_pointes || 0) > 1 ? 's' : ''} absent{(jour.absents_pointes || 0) > 1 ? 's' : ''}</p>
                                                <p><span className="font-semibold text-amber-600">{jour.aucun_pointage || 0}</span> employé{(jour.aucun_pointage || 0) > 1 ? 's' : ''} sans pointage</p>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-red-200 transform rotate-45"></div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="text-center py-4 px-4">
                                  {isFuture ? <span className="text-xs text-gray-400 font-medium">-</span> : (
                                    <span className={`inline-flex items-center justify-center w-12 h-12 rounded-xl font-bold text-lg ${(jour.retards || 0) === 0 ? 'bg-gray-50 text-gray-300' : 'bg-orange-100 text-orange-700'
                                      }`}>{jour.retards || 0}</span>
                                  )}
                                </td>
                                <td className="text-center py-4 px-4">
                                  <div className="flex flex-col items-center gap-2">
                                    <span className={`inline-block px-4 py-2 rounded-xl text-sm font-bold ${jour.taux_presence >= 90 ? 'bg-green-100 text-green-700' :
                                      jour.taux_presence >= 70 ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-red-100 text-red-700'
                                      }`}>{jour.taux_presence}%</span>
                                    <div className="w-full max-w-[100px] h-2 bg-gray-200 rounded-full overflow-hidden">
                                      <div className="h-full rounded-full transition-all" style={{
                                        width: `${jour.taux_presence}%`,
                                        backgroundColor: jour.taux_presence >= 90 ? '#22c55e' : jour.taux_presence >= 70 ? '#eab308' : '#ef4444'
                                      }}></div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Résumé du mois */}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
                        <div className="text-xs font-semibold text-gray-600 mb-1">Meilleur taux</div>
                        <div className="text-2xl font-bold text-green-600">{Math.max(...rapportMois.jours.map(j => j.taux_presence))}%</div>
                      </div>
                      <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
                        <div className="text-xs font-semibold text-gray-600 mb-1">Taux moyen</div>
                        <div className="text-2xl font-bold text-gray-900">{Math.round(rapportMois.jours.reduce((sum, j) => sum + j.taux_presence, 0) / rapportMois.jours.length)}%</div>
                      </div>
                      <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
                        <div className="text-xs font-semibold text-gray-600 mb-1">Pire taux</div>
                        <div className="text-2xl font-bold text-red-600">{Math.min(...rapportMois.jours.map(j => j.taux_presence))}%</div>
                      </div>
                      <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
                        <div className="text-xs font-semibold text-gray-600 mb-1">Jours analysés</div>
                        <div className="text-2xl font-bold text-gray-900">{rapportMois.jours.length}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* VUE EMPLOYÉS FANTÔMES */}
          {fantomesEnabled && viewMode === 'fantomes' && employesFantomes && (
            <div className="space-y-6">

              {/* Compteurs */}
              <div>
                <div className="px-6 py-4 mb-4 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#8b5158' }}>
                  <Users size={24} className="text-white" />
                  <h2 className="font-bold text-white text-xl">Employés fantômes</h2>

                  <span className="ml-auto px-3 py-1 bg-white/20 rounded-full text-white text-sm font-bold">
                    {employesFantomes.total} employé{employesFantomes.total > 1 ? 's' : ''} concerné{employesFantomes.total > 1 ? 's' : ''}
                  </span>

                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <CardIndicateur
                    icon={Users}
                    label="Jamais pointé"
                    value={employesFantomes.jamais_pointe.length}
                    subValue="Aucun pointage depuis leur embauche"
                    color="#8b5158"
                  />
                  <CardIndicateur
                    icon={Clock}
                    label="Inactifs depuis 30 jours"
                    value={employesFantomes.inactifs_30j.length}
                    subValue="Aucun pointage récent"
                    color="#f97316"
                  />
                </div>
              </div>

              {/* Message si aucun fantôme */}
              {employesFantomes.total === 0 && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  <p className="text-lg font-bold text-green-700">Tous les employés sont actifs</p>
                  <p className="text-sm text-gray-500 mt-1">Aucun employé sans pointage récent</p>
                </div>
              )}

              {/* Tableau jamais pointé */}
              {employesFantomes.jamais_pointe.length > 0 && (
                <div>
                  <div className="px-6 py-4 mb-4 rounded-xl flex items-center gap-3"
                    style={{ backgroundColor: '#f9f0f1', border: '1.5px solid #c49097' }}>
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#8b5158' }} />
                    <h3 className="font-bold text-base" style={{ color: '#8b5158' }}>
                      Jamais pointé - {employesFantomes.jamais_pointe.length} employé{employesFantomes.jamais_pointe.length > 1 ? 's' : ''}
                    </h3>
                  </div>
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600">Employé</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600">Département</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600">Poste</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600">Date d'embauche</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600">Jours sans pointage</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600">Statut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {employesFantomes.jamais_pointe
                          .slice((pageFantomesJamais - 1) * LIMIT_FANTOMES, pageFantomesJamais * LIMIT_FANTOMES)
                          .map(emp => (
                            <tr key={emp.id} className="hover:bg-gray-50/50 transition-colors">
                              <td className="px-5 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-full flex items-center justify-center"
                                    style={{ backgroundColor: '#f9f0f1' }}>
                                    <Users size={16} style={{ color: '#8b5158' }} />
                                  </div>
                                  <div>
                                    <div className="text-sm font-bold text-gray-900">{emp.first_name} {emp.last_name}</div>
                                    {emp.badge_number && <div className="text-xs text-gray-400">Badge: {emp.badge_number}</div>}
                                  </div>
                                </div>
                              </td>
                              <td className="px-5 py-3">
                                <span className="px-2 py-1 text-xs font-medium rounded-lg bg-gray-100 text-gray-700">
                                  {emp.department || 'N/A'}
                                </span>
                              </td>
                              <td className="px-5 py-3 text-sm text-gray-600">{emp.position || 'N/A'}</td>
                              <td className="px-5 py-3 text-sm text-gray-600">
                                {emp.hire_date ? new Date(emp.hire_date).toLocaleDateString('fr-FR') : 'N/A'}
                              </td>
                              <td className="px-5 py-3">
                                <span className="px-2 py-1 text-xs font-bold rounded-lg"
                                  style={{ backgroundColor: '#f9f0f1', color: '#8b5158' }}>
                                  {emp.jours_depuis_embauche} jour{emp.jours_depuis_embauche > 1 ? 's' : ''}
                                </span>
                              </td>
                              <td className="px-5 py-3">
                                <span className="px-2 py-1 text-xs font-semibold rounded-lg"
                                  style={{ backgroundColor: '#f9f0f1', color: '#8b5158' }}>
                                  Jamais pointé
                                </span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>

                    <Pagination
                      page={pageFantomesJamais}
                      totalPages={Math.ceil(employesFantomes.jamais_pointe.length / LIMIT_FANTOMES)}
                      total={employesFantomes.jamais_pointe.length}
                      limit={LIMIT_FANTOMES}
                      onPageChange={setPageFantomesJamais}
                      itemLabel="employé"
                    />
                  </div>
                </div>
              )}

              {/* Tableau inactifs 30j */}
              {employesFantomes.inactifs_30j.length > 0 && (
                <div>
                  <div className="px-6 py-4 mb-4 rounded-xl flex items-center gap-3"
                    style={{ backgroundColor: '#fff7ed', border: '1.5px solid #fdba74' }}>
                    <div className="w-3 h-3 rounded-full bg-orange-500" />
                    <h3 className="font-bold text-orange-700 text-base">
                      Inactifs depuis 30 jours - {employesFantomes.inactifs_30j.length} employé{employesFantomes.inactifs_30j.length > 1 ? 's' : ''}
                    </h3>
                  </div>
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600">Employé</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600">Département</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600">Poste</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600">Dernier pointage</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600">Jours d'inactivité</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600">Statut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {employesFantomes.inactifs_30j
                          .slice((pageFantomesInactifs - 1) * LIMIT_FANTOMES, pageFantomesInactifs * LIMIT_FANTOMES)
                          .map(emp => {
                            const joursInactivite = emp.dernier_pointage
                              ? Math.ceil((new Date() - new Date(emp.dernier_pointage)) / (1000 * 60 * 60 * 24))
                              : emp.jours_depuis_embauche;
                            return (
                              <tr key={emp.id} className="hover:bg-gray-50/50 transition-colors">
                                <td className="px-5 py-3">
                                  <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-full flex items-center justify-center bg-orange-100">
                                      <Users size={16} className="text-orange-600" />
                                    </div>
                                    <div>
                                      <div className="text-sm font-bold text-gray-900">{emp.first_name} {emp.last_name}</div>
                                      {emp.badge_number && <div className="text-xs text-gray-400">Badge: {emp.badge_number}</div>}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-5 py-3">
                                  <span className="px-2 py-1 text-xs font-medium rounded-lg bg-gray-100 text-gray-700">
                                    {emp.department || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-5 py-3 text-sm text-gray-600">{emp.position || 'N/A'}</td>
                                <td className="px-5 py-3 text-sm text-gray-600">
                                  {emp.dernier_pointage ? new Date(emp.dernier_pointage).toLocaleDateString('fr-FR') : 'N/A'}
                                </td>
                                <td className="px-5 py-3">
                                  <span className="px-2 py-1 text-xs font-bold rounded-lg bg-orange-100 text-orange-700">
                                    {joursInactivite} jour{joursInactivite > 1 ? 's' : ''}
                                  </span>
                                </td>
                                <td className="px-5 py-3">
                                  <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-orange-100 text-orange-700">
                                    Inactif +30j
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>

                    <Pagination
                      page={pageFantomesInactifs}
                      totalPages={Math.ceil(employesFantomes.inactifs_30j.length / LIMIT_FANTOMES)}
                      total={employesFantomes.inactifs_30j.length}
                      limit={LIMIT_FANTOMES}
                      onPageChange={setPageFantomesInactifs}
                      itemLabel="employé"
                    />
                  </div>
                </div>
              )}

            </div>
          )}


        </>
      )}

      {/* Toast */}
      {toast.show && (
        <Toast
          show={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: '' })}
        />
      )}

      {showGuide && (
        <GuideRapportsStatistiques
          onClose={() => setShowGuide(false)}
          comptaEnabled={comptaEnabled}
        />
      )}
    </div>
  );
};

export default RapportsRH;